"use client";

import { motion } from "motion/react";
import { useEffect, useState } from "@wordpress/element";

export default function AnimatedToggleSwitch() {
	const [isOn, setIsOn] = useState(false);

	// Auto-toggle every 2 seconds for continuous animation
	useEffect(() => {
		const interval = setInterval(() => {
			setIsOn((prev) => !prev);
		}, 2000);

		return () => clearInterval(interval);
	}, []);

	return (
		<div className="qc:relative">
			<svg
				width="146"
				height="144"
				viewBox="0 0 73 72"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				className="scale-150 zn:mx-auto"
			>
				{/* Background track */}
				<motion.path
					d="M71.7471 34.9008C71.7471 44.3484 64.0882 52.0071 54.6407 52.0071H18.3592C8.91161 52.0071 1.25293 44.3484 1.25293 34.9008C1.25293 25.4533 8.91161 17.7944 18.3592 17.7944H54.6407C64.0882 17.7944 71.7471 25.4533 71.7471 34.9008Z"
					fill={isOn ? "#10B981" : "#E5E7EB"}
					animate={{
						fill: isOn ? "#10B981" : "#E5E7EB",
					}}
					transition={{ duration: 0.3 }}
				/>

				{/* Border */}
				<path
					d="M54.6405 53.1386H18.3589C8.30284 53.1386 0.121094 44.9569 0.121094 34.9001C0.121094 24.8434 8.30284 16.6616 18.3589 16.6616H54.6405C64.6973 16.6616 72.8791 24.8434 72.8791 34.9001C72.8791 44.9569 64.6973 53.1386 54.6405 53.1386ZM18.3589 18.9258C9.55063 18.9258 2.38524 26.0919 2.38524 34.9001C2.38524 43.7083 9.55063 50.8745 18.3589 50.8745H54.6405C63.4488 50.8745 70.6149 43.7083 70.6149 34.9001C70.6149 26.0919 63.4488 18.9258 54.6405 18.9258H18.3589Z"
					fill="#111927"
				/>

				{/* Animated toggle circle */}
				<motion.g
					animate={{
						x: isOn ? 25 : 0,
					}}
					transition={{
						type: "spring",
						stiffness: 500,
						damping: 30,
					}}
				>
					<motion.path
						d="M29.8336 34.8942C29.8336 40.7886 25.0566 45.5777 19.1625 45.5777C13.268 45.5777 8.49121 40.7886 8.49121 34.8942C8.49121 29.0121 13.268 24.2231 19.1625 24.2231C25.0566 24.2231 29.8336 29.0121 29.8336 34.8942Z"
						fill={isOn ? "#FFFFFF" : "#B2CCFF"}
						animate={{
							fill: isOn ? "#FFFFFF" : "#B2CCFF",
							scale: isOn ? 1.05 : 1,
						}}
						transition={{ duration: 0.3 }}
					/>
					<path
						d="M19.1629 46.7106C12.6542 46.7106 7.35938 41.4106 7.35938 34.8953C7.35938 28.3866 12.6542 23.0918 19.1629 23.0918C25.6716 23.0918 30.9664 28.3866 30.9664 34.8953C30.9664 41.4106 25.6716 46.7106 19.1629 46.7106ZM19.1629 25.3559C13.9027 25.3559 9.62353 29.6351 9.62353 34.8953C9.62353 40.1621 13.9027 44.4465 19.1629 44.4465C24.4231 44.4465 28.7022 40.1621 28.7022 34.8953C28.7022 29.6351 24.4231 25.3559 19.1629 25.3559Z"
						fill="#111927"
					/>
				</motion.g>

				{/* Lightning bolt - animated opacity */}
				<motion.g
					animate={{
						opacity: isOn ? 1 : 0.3,
						scale: isOn ? 1.1 : 1,
					}}
					transition={{ duration: 0.3 }}
				>
					<path
						d="M15.0778 14.3388C14.8973 14.3388 14.7145 14.296 14.5442 14.2047C13.9929 13.9091 13.7851 13.2229 14.0806 12.6724L15.7596 9.53779H12.5056C12.0936 9.53779 11.714 9.31374 11.515 8.95333C11.3153 8.59219 11.3278 8.15218 11.5475 7.80357L15.9527 0.7959C16.2851 0.264502 16.986 0.106041 17.513 0.439915C18.0429 0.772315 18.2021 1.47102 17.869 2.0002L14.5545 7.27364H17.6508C18.0488 7.27364 18.4173 7.48296 18.6222 7.8242C18.8264 8.16545 18.8367 8.58924 18.6487 8.94006L16.0765 13.7411C15.8723 14.1221 15.4817 14.3388 15.0778 14.3388Z"
						fill={isOn ? "#FCD34D" : "#111927"}
					/>
					<path
						d="M6.37034 17.0391C5.79841 17.0391 5.30681 16.6072 5.24564 16.025L4.87417 12.4894L2.18255 14.318C1.84278 14.5494 1.40351 14.5774 1.03426 14.391C0.667216 14.2045 0.429893 13.833 0.415153 13.421L0.121815 5.14862C0.0997047 4.52436 0.588354 3.9996 1.21335 3.97749C1.8332 3.92737 2.36238 4.44402 2.38449 5.06902L2.6056 11.2932L5.16677 9.5531C5.49623 9.33126 5.91928 9.29588 6.27969 9.46319C6.64083 9.63049 6.887 9.97542 6.92901 10.3719L7.498 15.7891C7.56285 16.4104 7.11179 16.9676 6.48974 17.0332C6.44994 17.0369 6.41014 17.0391 6.37034 17.0391Z"
						fill={isOn ? "#FCD34D" : "#111927"}
					/>
				</motion.g>

				{/* Cursor pointer */}
				<motion.g
					animate={{
						scale: isOn ? 1.1 : 1,
						rotate: isOn ? 5 : -5,
					}}
					transition={{ duration: 0.3 }}
				>
					<path
						d="M68.9497 54.0827L56.5476 56.1138C55.7236 56.2488 55.0255 56.795 54.6963 57.5624L49.7374 69.1224C48.7662 71.3864 45.4347 70.9544 45.073 68.5174L41.1019 41.7532C40.8237 39.8776 42.694 38.4131 44.448 39.1331L69.4829 49.4092C71.7627 50.3451 71.3818 53.6844 68.9497 54.0827Z"
						fill="#FF9C61"
					/>
					<path
						d="M47.5063 71.7356C47.3493 71.7356 47.1909 71.726 47.0309 71.7054C45.4272 71.4968 44.1904 70.2829 43.9531 68.6836L39.982 41.9184C39.7911 40.6293 40.2893 39.3808 41.3153 38.5774C42.3405 37.7741 43.673 37.5891 44.8773 38.0851L69.912 48.3615C71.4089 48.9754 72.2911 50.4679 72.1076 52.0754C71.9248 53.6821 70.7293 54.938 69.1322 55.1996L56.7302 57.2309C56.2858 57.3038 55.9143 57.5942 55.736 58.0084L50.778 69.5687C50.2038 70.9064 48.9258 71.7356 47.5063 71.7356ZM43.5131 40.0765C43.1269 40.0765 42.8402 40.2593 42.7105 40.3603C42.5299 40.5018 42.1201 40.9042 42.2211 41.5867L46.1922 68.3512C46.2998 69.0742 46.8467 69.3977 47.3221 69.4597C47.7938 69.5164 48.4077 69.3476 48.6966 68.6755L53.6553 57.1152C54.1403 55.9868 55.153 55.1945 56.3647 54.9962L68.7666 52.965C69.4889 52.8463 69.8036 52.295 69.8582 51.8189C69.9127 51.342 69.7292 50.734 69.0526 50.4561L44.018 40.1797C43.8367 40.1053 43.6671 40.0765 43.5131 40.0765Z"
						fill="#111927"
					/>
				</motion.g>
			</svg>

			{/* Glow effect when on */}
			{isOn && (
				<motion.div
					className="qc:absolute qc:inset-0 qc:rounded-full"
					style={{
						background:
							"radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%)",
						filter: "blur(20px)",
					}}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.3 }}
				/>
			)}

			{/* Status text */}
			<motion.div
				className="qc:text-center qc:mt-8"
				animate={{
					color: isOn ? "#10B981" : "#6B7280",
				}}
			>
				<motion.p
					className="qc:text-2xl qc:font-bold"
					animate={{
						scale: isOn ? 1.05 : 1,
					}}
					transition={{ duration: 0.3 }}
				>
					{isOn ? "ON" : "OFF"}
				</motion.p>
			</motion.div>
		</div>
	);
}
